import React, { memo } from 'react';

import { Box, Typography } from '@mui/material';
import StatusIconCDS from 'app/shared/componentsRestyled/coreDesignSystem/StatusIconCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { AiFormDiffRowState } from 'constants/aiProcessRuns';
import { StatusEnum } from 'constants/status';

import CitedText from '../../../../AiActivityCitations/CitedText';
import {
  DIFF_STATE_LABELS,
  DIFF_UNRESOLVED_REASON_LABELS,
  FORM_REVIEW_LABELS,
  FormReviewTestIds,
} from '../FormReviewCard.constants';
import {
  DIFF_ROW_TONE,
  diffStateCellStyles,
  getDiffRowStyles,
  tableCellStyles,
  tableRowStyles,
  tableStyles,
} from '../FormReviewCard.styles';

const DIFF_STATE_TO_TONE = {
  [AiFormDiffRowState.MATCHED]: DIFF_ROW_TONE.MATCHED,
  [AiFormDiffRowState.AI_ONLY]: DIFF_ROW_TONE.AI_ONLY,
  [AiFormDiffRowState.RECORD_ONLY]: DIFF_ROW_TONE.RECORD_ONLY,
};

const formatValues = values => {
  if (_.isNil(values)) {
    return FORM_REVIEW_LABELS.NO_VALUE;
  }

  if (_.isString(values) || _.isNumber(values) || _.isBoolean(values)) {
    return String(values);
  }

  const entries = Object.entries(values);

  if (entries.length === 0) {
    return FORM_REVIEW_LABELS.NO_VALUE;
  }

  return entries.map(([key, value]) => `${key}: ${formatValues(value)}`).join(', ');
};

const formatVariance = monthlyVariance =>
  _.isNumber(monthlyVariance) ? String(monthlyVariance) : FORM_REVIEW_LABELS.NO_VALUE;

const headerStyles = ({ palette }) => ({
  ...tableRowStyles({ palette }),
  fontWeight: 700,
});

const FormFieldDiffTable = ({ rows }) => (
  <Box data-testid={FormReviewTestIds.DIFF_TABLE} sx={tableStyles}>
    <Box sx={headerStyles}>
      <Box sx={diffStateCellStyles}>
        <Typography variant="bold12">{FORM_REVIEW_LABELS.DIFF_STATE_HEADING}</Typography>
      </Box>
      <Box sx={tableCellStyles}>
        <Typography variant="bold12">{FORM_REVIEW_LABELS.DIFF_RECORD_HEADING}</Typography>
      </Box>
      <Box sx={tableCellStyles}>
        <Typography variant="bold12">{FORM_REVIEW_LABELS.DIFF_AI_HEADING}</Typography>
      </Box>
      <Box sx={tableCellStyles}>
        <Typography variant="bold12">{FORM_REVIEW_LABELS.DIFF_VARIANCE_HEADING}</Typography>
      </Box>
    </Box>
    {rows.map((row, rowIndex) => {
      const tone = DIFF_STATE_TO_TONE[row?.state] ?? DIFF_ROW_TONE.MATCHED;
      const isMaterial = row?.isMaterialDifference === true;
      const unresolvedLabel = row?.unresolvedReason
        ? (DIFF_UNRESOLVED_REASON_LABELS[row.unresolvedReason] ?? row.unresolvedReason)
        : null;

      return (
        <Box
          data-testid={FormReviewTestIds.DIFF_ROW}
          // eslint-disable-next-line react/no-array-index-key
          key={rowIndex}
          sx={getDiffRowStyles(tone)}
        >
          <Box sx={diffStateCellStyles}>
            <Typography variant="regular12">
              {DIFF_STATE_LABELS[row?.state] ?? row?.state ?? FORM_REVIEW_LABELS.NO_VALUE}
            </Typography>
            {unresolvedLabel ? (
              <Typography color="warning.dark" variant="regular12">
                {unresolvedLabel}
              </Typography>
            ) : null}
          </Box>
          <Box sx={tableCellStyles}>
            <Typography variant="regular12">
              <CitedText text={formatValues(row?.recordValues)} />
            </Typography>
          </Box>
          <Box sx={tableCellStyles}>
            <Typography variant="regular12">
              <CitedText text={formatValues(row?.aiValues)} />
            </Typography>
          </Box>
          <Box sx={tableCellStyles}>
            {isMaterial ? <StatusIconCDS size={16} type={StatusEnum.WARNING} /> : null}
            <Typography color={isMaterial ? 'warning.dark' : 'text.secondary'} variant="regular12">
              {formatVariance(row?.monthlyVariance)}
            </Typography>
          </Box>
        </Box>
      );
    })}
  </Box>
);

FormFieldDiffTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      state: PropTypes.string,
      changed: PropTypes.bool,
      aiValues: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.number]),
      recordValues: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.number]),
      monthlyVariance: PropTypes.number,
      isMaterialDifference: PropTypes.bool,
      unresolvedReason: PropTypes.string,
    }),
  ).isRequired,
};

export default memo(FormFieldDiffTable);
